import { Button } from '@heroui/react'
import { authService } from '@qse/auth-ui'
import { type ModalComponentProps } from '@qse/constants'
import { FormikControl } from '@qse/ui'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import * as Yup from 'yup'

interface TwoFAVerifyFormValues {
  code: string
}

const initialValues: TwoFAVerifyFormValues = {
  code: '',
}

const validationSchema = Yup.object({
  code: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Please enter a valid 6-digit code')
    .length(6, 'Code must be exactly 6 digits'),
})

export default function TwoFAVerify(props: ModalComponentProps) {
  const { setModelActive, onClose, data } = props
  const [isLoading, setIsLoading] = useState(false)
  const [isVerifyComplete, setIsVerifyComplete] = useState(false)

  // Check if this verification is coming from password change
  const fromPasswordChange = data?.fromPasswordChange || false
  const customMessage = data?.message

  const handleSubmit = async (
    values: TwoFAVerifyFormValues,
    {
      setSubmitting,
      resetForm,
      setStatus,
    }: {
      setSubmitting: (submitting: boolean) => void
      resetForm: () => void
      setStatus: (status: string) => void
    },
  ) => {
    try {
      setIsLoading(true)
      await authService.confirmTOTPCode(values.code)
      setIsVerifyComplete(true)
      resetForm()
      // Close modal after a short delay to show success message
      setTimeout(() => {
        setModelActive(undefined)
        onClose()
      }, 2000)
    } catch (error) {
      // Use the specific MFA verification error handling
      authService.handleError(error, 'mfaVerification', setStatus)
    } finally {
      setSubmitting(false)
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setModelActive(undefined)
    onClose()
  }

  if (isVerifyComplete) {
    return (
      <div className="flex flex-col gap-5 py-5 px-8 w-full">
        <div className="flex flex-col gap-2 justify-center items-center text-current">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M5 13l4 4L19 7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <p className="font-medium text-2xl">Verification Successful!</p>
          <p className="text-sm font-medium text-center text-gray-600 dark:text-gray-400">
            Your identity has been verified successfully.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid, dirty, status }) => (
          <Form className="flex flex-col gap-5 py-5 px-8 w-full">
            <div className="flex flex-col gap-2 justify-center items-center text-current">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>
              <p className="font-medium text-2xl">Two-Factor Authentication</p>
              <p className="text-sm font-medium text-center text-gray-600 dark:text-gray-400">
                {fromPasswordChange
                  ? customMessage ||
                    'Your password has been updated. Please verify your 2FA code to continue.'
                  : 'Enter the 6-digit code from your authenticator app'}
              </p>
            </div>

            {status && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{status}</p>
              </div>
            )}

            <FormikControl
              isRequired
              control="text"
              description="Enter the 6-digit code from your authenticator app"
              label="Verification Code"
              name="code"
              placeholder="Enter 6-digit code"
            />

            <div className="flex justify-end gap-3 w-full">
              <Button disabled={isLoading} radius="sm" variant="flat" onPress={handleCancel}>
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={!isValid || !dirty || isLoading}
                isLoading={isSubmitting}
                radius="sm"
                type="submit"
              >
                {isSubmitting ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}
